import React, { useEffect, useState } from "react";
import { Form, Input, Select, Button, Segmented, Alert, App } from "antd";
import { createDemoSchool } from "../../../services/demo/demo.service";
import { usePrograms } from "../../../hooks/usePrograms";
import { useSubjects } from "../../../hooks/useSubject";

const GRADE_OPTIONS = [
  { value: "K1", label: "K1 — Kinder 1" },
  { value: "K2", label: "K2 — Kinder 2" },
  { value: "K3", label: "K3 — Kinder 3" },
  { value: "P1", label: "P1 — Primaria 1°" },
  { value: "P2", label: "P2 — Primaria 2°" },
  { value: "P3", label: "P3 — Primaria 3°" },
  { value: "P4", label: "P4 — Primaria 4°" },
  { value: "P5", label: "P5 — Primaria 5°" },
  { value: "P6", label: "P6 — Primaria 6°" },
];

export interface CreateDemoSchoolViewProps {
  onCreated: (credentials: {
    email: string;
    password: string;
    schoolName: string;
    classroomsCreated: number;
  }) => void;
}

const CreateDemoSchoolView: React.FC<CreateDemoSchoolViewProps> = ({
  onCreated,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<number[]>([]);
  const [programSort, setProgramSort] = useState<"subject" | "grade">(
    "subject"
  );
  const { programs, refetch: refetchPrograms } = usePrograms();
  const { subjects } = useSubjects();

  // This view must reflect programs created elsewhere in the back-office, so
  // pull a fresh list on mount instead of trusting the Redux cache.
  useEffect(() => {
    refetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Canonical grade order taken from GRADE_OPTIONS (K1→K3, P1→P6).
  const gradeRank = (code: string) => {
    const i = GRADE_OPTIONS.findIndex((g) => g.value === code);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };

  const availableProgramOptions = (programs ?? [])
    .filter((p) => selectedGrades.includes(p.grade.code))
    .slice()
    .sort((a, b) => {
      const bySubject = (a.subject?.name ?? "").localeCompare(
        b.subject?.name ?? ""
      );
      const byGrade = gradeRank(a.grade.code) - gradeRank(b.grade.code);
      // Within a subject, grades run ascending; within a grade, subjects A→Z.
      return programSort === "subject"
        ? bySubject || byGrade
        : byGrade || bySubject;
    })
    .map((p) => ({ value: p.id, label: p.title }));

  // Subjects offered are derived from the nested subject the program list
  // actually carries (GET /programs returns `subject: { id, name }`, no flat
  // subject_id). useSubjects only supplies the `hidden` flag to filter out.
  const hiddenSubjectIds = new Set(
    (subjects ?? []).filter((s) => s.hidden).map((s) => s.id)
  );

  // SEL is identified by subject code (the backend's canonical key). Its
  // programs are always auto-included when a grade is chosen, regardless of
  // the Asignaturas selector — the operator can still remove them manually.
  const selSubjectId = (subjects ?? []).find((s) => s.code === "SEL")?.id;
  const subjectsInGrades = new Map<number, string>();
  (programs ?? [])
    .filter((p) => selectedGrades.includes(p.grade.code))
    .forEach((p) => {
      if (p.subject && !hiddenSubjectIds.has(p.subject.id)) {
        subjectsInGrades.set(p.subject.id, p.subject.name);
      }
    });
  const availableSubjectOptions = Array.from(
    subjectsInGrades,
    ([value, label]) => ({ value, label })
  ).sort((a, b) => a.label.localeCompare(b.label));

  // Ambiguity: when a chosen grade × subject pair matches more than one
  // program, auto-selection grabs them all. Surface those collisions so the
  // operator can verify and trim to the right programs.
  const ambiguousGroups = selectedSubjects.flatMap((subjectId) =>
    selectedGrades
      .map((gradeCode) => {
        const matches = (programs ?? []).filter(
          (p) =>
            p.grade.code === gradeCode &&
            p.subject != null &&
            p.subject.id === subjectId
        );
        return matches.length > 1
          ? {
              key: `${gradeCode}-${subjectId}`,
              subjectName: matches[0].subject!.name,
              gradeName: matches[0].grade.grade,
              items: matches.map((p) => ({
                title: p.title,
                code: p.program_code,
              })),
            }
          : null;
      })
      .filter((g): g is NonNullable<typeof g> => g !== null)
  );

  // Program IDs matching a grade × subject combination.
  const programIdsFor = (
    gradeCodes: string[],
    subjectIds: number[]
  ): number[] =>
    (programs ?? [])
      .filter(
        (p) =>
          gradeCodes.includes(p.grade.code) &&
          p.subject != null &&
          subjectIds.includes(p.subject.id)
      )
      .map((p) => p.id);

  const handleSubmit = async (values: {
    schoolName: string;
    grades: string[];
    programIds: (number | string)[];
    firstName: string;
    lastName: string;
    email: string;
  }) => {
    setSubmitting(true);
    try {
      const result = await createDemoSchool({
        schoolName: values.schoolName,
        grades: values.grades,
        // Coerce to numbers: AntD Select stores numeric option values as
        // numbers in production, but the MockSelect used in tests passes
        // string values from the native DOM <select>.
        programIds: values.programIds.map(Number),
        client: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
        },
      });
      form.resetFields();
      // resetFields only clears AntD-managed fields; the grade/subject state
      // lives in separate useState and would otherwise keep stale values,
      // leaving the dependent selects enabled with options from the prior grades.
      setSelectedGrades([]);
      setSelectedSubjects([]);
      onCreated(result.provisionResult);
    } catch (error: any) {
      const status = error?.response?.status;
      if (!error?.response) {
        message.error("Sin conexión. Reintentá.");
      } else if (status === 409) {
        form.setFields([
          {
            name: "email",
            errors: [
              "Este correo ya está en uso. Usá un email diferente para el docente.",
            ],
          },
        ]);
        form.scrollToField("email");
      } else if (status === 400) {
        const serverMsg =
          error?.response?.data?.message ?? "Datos inválidos. Verificá el formulario.";
        form.setFields([
          {
            name: "grades",
            errors: [serverMsg],
          },
        ]);
      } else {
        message.error("Error al crear la escuela demo. Intentá de nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const noGradesSelected = selectedGrades.length === 0;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      requiredMark={false}
      onValuesChange={(changedValues) => {
        if ("grades" in changedValues) {
          const newGrades: string[] = changedValues.grades ?? [];
          const addedGrades = newGrades.filter(
            (g) => !selectedGrades.includes(g)
          );
          setSelectedGrades(newGrades);

          // Drop subjects that no longer have any program in the remaining
          // grades, so the chips never lie about what's selectable.
          const validSubjectIds = new Set(
            (programs ?? [])
              .filter((p) => newGrades.includes(p.grade.code))
              .map((p) => p.subject?.id)
          );
          const nextSubjects = selectedSubjects.filter((id) =>
            validSubjectIds.has(id)
          );
          if (nextSubjects.length !== selectedSubjects.length) {
            setSelectedSubjects(nextSubjects);
            form.setFieldsValue({ subjects: nextSubjects });
          }

          // Recompute programs: keep picks whose grade is still selected, then
          // auto-add matches for already-chosen subjects in newly added grades.
          const current: number[] = (
            form.getFieldValue("programIds") ?? []
          ).map(Number);
          const validProgramIds = new Set(
            (programs ?? [])
              .filter((p) => newGrades.includes(p.grade.code))
              .map((p) => p.id)
          );
          const kept = current.filter((id) => validProgramIds.has(id));
          const autoAdd = programIdsFor(addedGrades, nextSubjects);
          // SEL programs auto-attach to every newly added grade.
          const selAdd =
            selSubjectId != null
              ? programIdsFor(addedGrades, [selSubjectId])
              : [];
          const next = Array.from(
            new Set([...kept, ...autoAdd, ...selAdd])
          );
          if (
            next.length !== current.length ||
            next.some((id) => !current.includes(id))
          ) {
            form.setFieldsValue({ programIds: next });
          }
        }

        if ("subjects" in changedValues) {
          // Coerce to numbers: AntD stores numeric option values as numbers in
          // production, but the MockSelect in tests yields string DOM values.
          const newSubjects: number[] = (changedValues.subjects ?? []).map(
            Number
          );
          const added = newSubjects.filter(
            (id) => !selectedSubjects.includes(id)
          );
          const removed = selectedSubjects.filter(
            (id) => !newSubjects.includes(id)
          );
          setSelectedSubjects(newSubjects);

          // Delta apply: selecting a subject adds its programs (within the
          // selected grades), deselecting removes them. Manual picks survive.
          const current: number[] = (
            form.getFieldValue("programIds") ?? []
          ).map(Number);
          const toRemove = new Set(programIdsFor(selectedGrades, removed));
          const toAdd = programIdsFor(selectedGrades, added);
          const next = Array.from(new Set([...current, ...toAdd])).filter(
            (id) => !toRemove.has(id)
          );
          form.setFieldsValue({ programIds: next });
        }
      }}
    >
      <Form.Item
        label="Nombre de la escuela"
        name="schoolName"
        rules={[
          { required: true, message: "Ingresá el nombre de la escuela" },
          { max: 120, message: "El nombre no puede superar 120 caracteres" },
        ]}
      >
        <Input placeholder="Ej: Escuela Demo Ventas" />
      </Form.Item>

      <Form.Item
        label="Grados"
        name="grades"
        rules={[
          { required: true, message: "Seleccioná al menos un grado" },
        ]}
      >
        <Select
          mode="multiple"
          placeholder="Seleccioná uno o más grados"
          optionFilterProp="label"
          options={GRADE_OPTIONS.map((g) => ({
            value: g.value,
            label: g.label,
            title: g.label,
          }))}
        />
      </Form.Item>

      <Form.Item
        label="Asignaturas"
        name="subjects"
        extra="Al elegir una asignatura se seleccionan automáticamente sus programas en los grados elegidos. Podés ajustar la selección manualmente abajo."
      >
        <Select
          mode="multiple"
          placeholder="Seleccioná asignaturas para autocompletar"
          optionFilterProp="label"
          disabled={noGradesSelected}
          options={availableSubjectOptions.map((o) => ({
            value: o.value,
            label: o.label,
            title: o.label,
          }))}
        />
      </Form.Item>

      <Form.Item label="Ordenar por">
        <Segmented
          value={programSort}
          onChange={(value) => setProgramSort(value as "subject" | "grade")}
          disabled={noGradesSelected}
          options={[
            { label: "Asignatura", value: "subject" },
            { label: "Grado", value: "grade" },
          ]}
        />
      </Form.Item>

      <Form.Item
        label="Programas"
        name="programIds"
        extra="Escribí para filtrar por nombre. Podés agregar o quitar programas a mano."
        rules={[
          { required: true, message: "Seleccioná al menos un programa" },
          {
            validator: (_, value: number[]) =>
              value && value.length > 0
                ? Promise.resolve()
                : Promise.reject(new Error("Seleccioná al menos un programa")),
          },
        ]}
      >
        <Select
          mode="multiple"
          placeholder="Seleccioná los programas"
          optionFilterProp="label"
          disabled={noGradesSelected}
          options={availableProgramOptions.map((o) => ({
            value: o.value,
            label: o.label,
            title: o.label,
          }))}
        />
      </Form.Item>

      {ambiguousGroups.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          message="Hay programas repetidos para los parámetros elegidos"
          description={
            <>
              <p style={{ marginBottom: 8 }}>
                Para estas combinaciones de grado y asignatura se autoseleccionó
                más de un programa. Revisá la lista de programas y dejá solo los
                que correspondan.
              </p>
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                {ambiguousGroups.map((g) => (
                  <li key={g.key}>
                    <strong>
                      {g.subjectName} · {g.gradeName}:
                    </strong>{" "}
                    {g.items
                      .map((i) => (i.code ? `${i.title} (${i.code})` : i.title))
                      .join(", ")}
                  </li>
                ))}
              </ul>
            </>
          }
        />
      )}

      <Form.Item
        label="Nombre del cliente"
        name="firstName"
        rules={[{ required: true, message: "Ingresá el nombre" }]}
      >
        <Input placeholder="Ej: María" />
      </Form.Item>

      <Form.Item
        label="Apellido del cliente"
        name="lastName"
        rules={[{ required: true, message: "Ingresá el apellido" }]}
      >
        <Input placeholder="Ej: González" />
      </Form.Item>

      <Form.Item
        label="Email del cliente"
        name="email"
        rules={[
          { required: true, message: "Ingresá el email" },
          { type: "email", message: "El formato de email no es válido" },
        ]}
      >
        <Input placeholder="Ej: maria.gonzalez@cliente.mx" />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={submitting}
          disabled={submitting}
        >
          Crear escuela demo
        </Button>
      </Form.Item>
    </Form>
  );
};

export default CreateDemoSchoolView;
